import { Link } from 'react-router-dom';
import { CreditCard, DollarSign, Loader2, Shield, Crown, Check, Dumbbell, Salad, LineChart, Infinity as InfinityIcon } from 'lucide-react';
import { Button } from '../ui/button';

const INCLUDED_FEATURES = (durationWeeks) => [
  {
    icon: Dumbbell,
    title: `${durationWeeks}-Week Structured Program`,
    desc: 'Full weekly schedule with workouts, sets, reps & rest days',
  },
  {
    icon: Salad,
    title: 'AI-Powered Nutrition Plan',
    desc: 'Personalized meals & macros tailored to your goals',
  },
  {
    icon: LineChart,
    title: 'Progress Tracker',
    desc: 'Daily check-ins, weekly stats & completion streaks',
  },
  {
    icon: InfinityIcon,
    title: 'Lifetime Access',
    desc: 'Own it forever — revisit, restart or repeat anytime',
  },
];

export const PurchaseOptions = ({ program, price, checkingOut, onStripe, onPayPal }) => (
  <>
    <h3 className="font-heading text-xl text-white uppercase tracking-wider mb-2">Get This Program</h3>

    {/* Price Display */}
    <div className="bg-black/50 p-4 mb-6 border border-primary/30">
      <div className="flex items-center justify-between mb-2">
        <span className="text-muted-foreground">One-time purchase</span>
        <div className="flex items-center gap-1">
          <DollarSign className="h-6 w-6 text-primary" />
          <span className="font-heading text-4xl text-primary">{price}</span>
        </div>
      </div>
      <p className="text-muted-foreground text-sm">
        Includes {program.duration_weeks} weeks of structured workouts
      </p>
    </div>

    {/* What's Included */}
    <div
      className="bg-gradient-to-br from-primary/10 via-black/40 to-black/60 border border-primary/25 p-5 mb-6"
      data-testid="whats-included-section"
    >
      <div className="flex items-center gap-2 mb-4">
        <div className="h-6 w-6 rounded-full bg-primary/20 flex items-center justify-center">
          <Check className="h-3.5 w-3.5 text-primary" strokeWidth={3} />
        </div>
        <h4 className="font-heading text-sm uppercase tracking-wider text-white">What&apos;s Included</h4>
      </div>

      <ul className="space-y-3">
        {INCLUDED_FEATURES(program.duration_weeks).map(({ icon: Icon, title, desc }) => (
          <li key={title} className="flex items-start gap-3" data-testid={`included-feature-${title.toLowerCase().replace(/\s+/g, '-')}`}>
            <div className="mt-0.5 h-8 w-8 shrink-0 rounded-md bg-primary/15 border border-primary/30 flex items-center justify-center">
              <Icon className="h-4 w-4 text-primary" />
            </div>
            <div className="min-w-0">
              <p className="text-white text-sm font-heading tracking-wide leading-tight">{title}</p>
              <p className="text-muted-foreground text-xs leading-snug mt-0.5">{desc}</p>
            </div>
          </li>
        ))}
      </ul>
    </div>

    <div className="space-y-3">
      <Button
        onClick={onStripe}
        className="w-full h-14 bg-[#635BFF] text-white font-heading text-lg tracking-wider uppercase hover:bg-[#635BFF]/90"
        disabled={checkingOut}
        data-testid="stripe-checkout-button"
      >
        {checkingOut ? <Loader2 className="mr-2 h-5 w-5 animate-spin" /> : <CreditCard className="mr-2 h-5 w-5" />}
        Pay with Card
      </Button>

      <Button
        onClick={onPayPal}
        className="w-full h-14 bg-[#003087] text-white font-heading text-lg tracking-wider uppercase hover:bg-[#003087]/90"
        disabled={checkingOut}
        data-testid="paypal-checkout-button"
      >
        {checkingOut ? (
          <Loader2 className="mr-2 h-5 w-5 animate-spin" />
        ) : (
          <svg className="mr-2 h-5 w-5" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
            <path d="M7.076 21.337H2.47a.641.641 0 0 1-.633-.74L4.944.901C5.026.382 5.474 0 5.998 0h7.46c2.57 0 4.578.543 5.69 1.81 1.01 1.15 1.304 2.42 1.012 4.287-.023.143-.047.288-.077.437-.983 5.05-4.349 6.797-8.647 6.797h-2.19c-.524 0-.968.382-1.05.9l-1.12 7.106zm14.146-14.42a3.35 3.35 0 0 0-.607-.541c-.013.076-.026.175-.041.254-.58 2.977-2.477 5.084-6.206 5.084h-2.19a1.88 1.88 0 0 0-1.857 1.587l-1.262 8.003h3.695c.457 0 .85-.334.919-.787l.038-.204.73-4.637.047-.255a.927.927 0 0 1 .917-.788h.578c3.745 0 6.68-1.523 7.537-5.927.311-1.597.206-2.924-.298-3.79z"/>
          </svg>
        )}
        Pay with PayPal
      </Button>
    </div>

    <div className="flex items-center gap-2 mt-4 text-muted-foreground text-sm">
      <Shield className="h-4 w-4" />
      <span>Secure checkout powered by Stripe & PayPal</span>
    </div>

    {/* Premium upsell */}
    <div className="mt-6 pt-6 border-t border-border">
      <div className="flex items-center gap-2 text-yellow-400 mb-3">
        <Crown className="h-5 w-5" />
        <span className="font-heading uppercase tracking-wider text-sm">Or Go Premium</span>
      </div>
      <p className="text-muted-foreground text-sm mb-3">
        Get access to ALL programs plus exclusive features for just $9.99/month
      </p>
      <Link to="/subscription">
        <Button variant="outline" className="w-full border-yellow-500/50 text-yellow-400 hover:bg-yellow-500/10">
          <Crown className="mr-2 h-4 w-4" />
          View Premium Plans
        </Button>
      </Link>
    </div>
  </>
);
